import React, { useContext, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext'; // Assuming the context is in AuthContext.tsx
import { confirmResetPassword, fetchAuthSession, type ConfirmResetPasswordInput } from 'aws-amplify/auth';
import { updatePassword, type UpdatePasswordInput } from 'aws-amplify/auth';
import { authGetUser, handleResetPassword } from '../services/authService';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { UserRoleContext } from '../context/UserRoleContext';
import loginBackground from '../assets/loginBackground.jpg';
import logo from '../assets/logo.png';
import { ROUTES } from '../constants/routes';

const LoginPage: React.FC = () => {
  const { signIn, error, loading, isAuthenticated, gotToVerify } = useAuth();  // Get signIn function, loading, and error states from AuthContext
  const [username, setUsername] = useState<string>('');  // Manage username input
  const [password, setPassword] = useState<string>('');  // Manage password input
  const [loginState, setLoginState] = useState<'LOGIN' | 'LOGGING_IN' | 'FORGOT_PASSWORD' | 'VERIFICATION'>('LOGIN');
  const [confirmationCode, setConfirmationCode] = useState<string>('');
  const { setUserRole } = useContext(UserRoleContext);
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Handle login form submission
  const handleLogin = async () => {
    setUserRole(null);
    await checkAuth();
    // Call the signIn function from the AuthContext
    if (username.length < 3 || password.length < 3) {
      if (username.length < 3) {
        addToast("Username error", true);
      }
      if (username.length < 3) {
        addToast("Password error", true);
      }
      return;
    }

    try {
      const user = await signIn(username, password);
      if (user) {
        // Redirect or show a success message on successful login
        setLoginState('LOGGING_IN');
        navigate(ROUTES.HOME);
      } else {
        if (gotToVerify) {
          addToast('Please verify your account.');
          navigate(ROUTES.VERIFY_ACCOUNT + '/' + username);
        } else {
          checkAuth();
        }

      }
    } catch (error) {
      if (error instanceof Error) {
        addToast(error.message, true);
      } else {
        addToast('Unknown error', true);
      }
      console.error(error);
    }
  };

  const handleCreateUserPressed = async () => {
    navigate(ROUTES.CREATE_ACCOUNT);
  }

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('login', Date.now().toString());
      navigate(ROUTES.HOME);

    }
  }, [isAuthenticated, loading]);

  const backToLogin = () => {
    setLoginState('LOGIN');
  }

  const checkAuth = async () => {
    let user = await fetchAuthSession();
    if (user.userSub) {
      navigate(ROUTES.HOME);
    }
  }

  useEffect(() => {
    checkAuth();
  }, [])

  const handleConfirmResetPassword = async () => {
    const newPassword = password;
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
      setLoginState('LOGIN');
      setUsername('');
      setPassword('');
      setConfirmationCode('');
    } catch (error) {
      if (error instanceof Error) {
        addToast(error.message, true);
      } else {
        addToast('Unknown password reset error', true);
      }
    }
  };


  async function handleUpdatePassword({
    oldPassword,
    newPassword
  }: UpdatePasswordInput) {
    try {
      await updatePassword({ oldPassword, newPassword });
    } catch (err) {
      console.log(err);
    }
  }

  const showForgotPassword = () => {
    setUserRole(null);
    setLoginState('FORGOT_PASSWORD');
  }


  const handleForgotPassword = async () => {
    setUserRole(null);
    setLoginState('FORGOT_PASSWORD');
    try {
      setPassword('');
      if (username.length > 2) {
        const result = await handleResetPassword(username);
        if (result === 'SENT') {
          addToast('Verification code sent');
          setLoginState('VERIFICATION');
        } else if (result === 'DONE') {
          addToast('Verification complete');
          setLoginState('LOGIN');
        } else {
          addToast('User does not exist', true);
          //toast Error
        }
      }
    } catch (error) {
      console.log(error);
    }
  };



  if (isAuthenticated) {
    // Redirect to dashboard or home if the user is already authenticated
    return <div>You're already logged in. Redirecting to the dashboard...</div>;
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 'calc(100vh - 100px)',
      backgroundImage: `url(${loginBackground})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      margin: '0px',
    }}>
      <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img src={logo} alt="Cyne Up" style={{ maxWidth: '75vw', width: '400px', justifyContent: 'center' }} />
        <div className='card' style={{ padding: '20px', width: '400px', backgroundColor: 'rgba(255, 255, 255, 1.0)', backdropFilter: 'blur(10px)', maxWidth: '80vw', marginTop: '10px' }}>
          <h1>{loginState === 'LOGIN' ? 'Login' : 'Reset Password'}</h1>
          {loginState != 'VERIFICATION' && <div>
            <label htmlFor="username">Email</label>
            <input
              type="text"
              id="username"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>}
          {(loginState === 'LOGGING_IN' || loginState === 'LOGIN' || loginState === 'VERIFICATION') && <div>
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (loginState == 'LOGIN') {
                    handleLogin();
                  }
                }
              }}
            />
          </div>}
          {(loginState === 'LOGIN' || loginState === 'LOGGING_IN') &&
            <button type="button" style={{ background: 'none', marginBottom: '5px', boxShadow: 'none' }} onClick={() => showForgotPassword()}>Forgot Password? Click Here</button>
          }

          {/* <div>
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div> */}

          {loginState === 'VERIFICATION' && <div>
            <label htmlFor="confirmationCode">Confirmation Code</label>
            <input
              type="text"
              id="confirmationCode"
              name="confirmationCode"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              required
            />
          </div>}

          <div className="buttonsRow">
            {(loginState === 'LOGIN') &&
              <button type="button" style={{ backgroundColor: '#51BCE4' }} onClick={() => handleCreateUserPressed()}>Create Account</button>
            }
            {(loginState === 'LOGIN' || loginState === 'LOGGING_IN') &&
              <button type="button" style={{ backgroundColor: '#51BCE4', minWidth: 120 }} onClick={() => handleLogin()}>Login</button>
            }


          </div>

          {loginState === 'FORGOT_PASSWORD' &&
            <div className='buttonsRow'>
              <button type="button" className='cancelButton' style={{ border: 'none', boxShadow: 'none' }} onClick={() => backToLogin()}>Back</button>
              <button type="button" style={{ backgroundColor: '#51BCE4' }} onClick={() => handleForgotPassword()} disabled={username.length < 2}>Send Reset</button>
            </div>
          }

          {loginState === 'VERIFICATION' &&
            <div className='buttonsRow'>
              <button type="button" className='cancelButton' onClick={() => backToLogin()}>Back</button>
              <button type="button" onClick={() => handleConfirmResetPassword()} disabled={username.length < 2}>Reset Password</button>
            </div>
          }
        </div>
              <h2 style={{ color: 'white' }}><i>The Fastest Cyber Sign Up</i></h2>
      </div>
    </div>
  );
};

export default LoginPage;
